<script lang="ts">
  import { format } from "date-fns";

  export let number_group_rides: number;
  export let biggestGroupRide: any;
  export let revealed: boolean = false;

  // Derived values
  const groupSize: number = biggestGroupRide?.group_activity_paths?.length ?? 1;
  const activity = biggestGroupRide;

  // Build map/chart image src (as requested)
  $: imageSrc = ((activity?.has_map_image !== false && activity?.map_url) ? activity?.map_url : activity?.chart_url)?.replace(/^.*(?=\/assets)/, '');

  // Participant bubbles (visual only)
  $: bubbleCount = Math.min(12, Math.max(6, groupSize));
  $: bubbles = Array.from({ length: bubbleCount }, (_, i) => ({
    id: i,
    side: i % 2 === 0 ? 'left' : 'right',
    top: 12 + (i * 6) % 56, // percentage within container
    size: 8 + (i % 5) * 3, // px
    delay: 80 + i * 110, // ms
    hue: (180 + i * 22) % 360,
  }));
</script>

<div class="group relative w-[300px] h-[460px] rounded-2xl overflow-hidden">
  <!-- Solid dark gradient + subtle pattern background -->
  <div class="card-bg absolute inset-0" />
  <!-- Peloton background layers -->
  <div class="absolute inset-0 pointer-events-none opacity-[0.18]">
    <div class="absolute -left-16 top-6 w-[360px] h-[120px] blur-[0.8px]" class:revealed={revealed}>
      <div class="peloton peloton-layer-1" class:animate-peloton-left={revealed} />
    </div>
    <div class="absolute -right-12 bottom-10 w-[320px] h-[100px] blur-[0.6px]" class:revealed={revealed}>
      <div class="peloton peloton-layer-2" class:animate-peloton-right={revealed} />
    </div>
  </div>

  <!-- Header removed: page already provides "Group Activities" -->

  <!-- Flowing info ribbons: clearly split + contextual labels -->
  <div class="relative z-10 px-4 pt-4 flex flex-col gap-2">
    <!-- Moving peloton strip -->
    <div class="flex flex-row-reverse gap-[18px] items-center justify-center opacity-[0.18]" aria-hidden="true">
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-swimming peloton-bike" style="animation-duration: 7000ms;"></i>
      </span>
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-swimming peloton-bike" style="animation-duration: 7300ms;"></i>
      </span>
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-swimming peloton-bike" style="animation-duration: 7600ms;"></i>
      </span>
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-swimming peloton-bike" style="animation-duration: 7900ms;"></i>
      </span>
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-swimming peloton-bike" style="animation-duration: 8100ms;"></i>
      </span>
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-swimming peloton-bike" style="animation-duration: 8400ms;"></i>
      </span>
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-swimming peloton-bike" style="animation-duration: 8700ms;"></i>
      </span>
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-swimming peloton-bike" style="animation-duration: 9000ms;"></i>
      </span>
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-swimming peloton-bike" style="animation-duration: 9300ms;"></i>
      </span>
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-swimming peloton-bike" style="animation-duration: 9600ms;"></i>
      </span>
    </div>

    <div class="ribbon ribbon-left" class:ribbon-in={revealed} style="--delay:120ms">
      <span class="ribbon-label">Total group activities</span>
      <span class="ribbon-value">{number_group_rides}</span>
      <!-- <span class="ribbon-unit">activities</span> -->
    </div>
    <div class="divider" aria-hidden="true"></div>

    <!-- Moving peloton strip -->
    <div class="flex flex-row gap-[18px] items-center justify-center opacity-[0.18]" aria-hidden="true">
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-biking peloton-bike" style="animation-duration: 1800ms; animation-direction: reverse;"></i>
      </span>
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-biking peloton-bike" style="animation-duration: 2000ms; animation-direction: reverse;"></i>
      </span>
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-biking peloton-bike" style="animation-duration: 2200ms; animation-direction: reverse;"></i>
      </span>
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-biking peloton-bike" style="animation-duration: 2400ms; animation-direction: reverse;"></i>
      </span>
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-biking peloton-bike" style="animation-delay: 200ms; animation-duration: 2400ms; animation-direction: reverse;"></i>
      </span>
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-biking peloton-bike" style="animation-delay: 400ms; animation-duration: 2400ms; animation-direction: reverse;"></i>
      </span>
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-biking peloton-bike" style="animation-duration: 2600ms; animation-direction: reverse;"></i>
      </span>
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-biking peloton-bike" style="animation-duration: 2800ms; animation-direction: reverse;"></i>
      </span>
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-biking peloton-bike" style="animation-duration: 3000ms; animation-direction: reverse;"></i>
      </span>
      <span style="display:inline-flex; transform: scaleX(-1);">
      <i class="fas fa-person-biking peloton-bike" style="animation-duration: 3200ms; animation-direction: reverse;"></i>
      </span>
    </div>

    <div class="ribbon ribbon-right" class:ribbon-in={revealed} style="--delay:240ms">
      <span class="ribbon-label">Biggest group activity</span>
      <span class="ribbon-value">{groupSize}</span>
      <span class="ribbon-unit">{groupSize === 1 ? 'person' : 'people'}</span>
    </div>
  </div>

  <!-- Map image container -->
  <div class="relative z-10 px-4 pt-3">
    <div class="map-frame overflow-hidden rounded-xl" class:map-in={revealed}>
      {#if imageSrc}
        <img class='w-full h-[180px] object-cover will-change-transform' src={imageSrc} alt='Activity' class:img-pop-in={revealed} />
      {:else}
        <div class="w-full h-[180px] flex items-center justify-center text-off-white/70">No image</div>
      {/if}
      <div class="map-overlay" />
    </div>
  </div>

  <!-- Participant bubbles join animation (removed) -->

  <!-- Activity meta integrated into background flow -->
  <div class="relative z-10 px-4 py-3">
    <div class="flex flex-col gap-1">
      <div class="meta-line" class:flow-in={revealed} style="--delay:320ms">
        <i class="fa-regular fa-map" style="color: var(--accent);"></i>
        <a class="text-sm font-semibold truncate" href={"/activities/" + activity.path} target='_blank'>{activity?.name ?? 'Big Group Activity'}</a>
      </div>
      <div class="meta-line" class:flow-in={revealed} style="--delay:420ms">
        <i class="fa-regular fa-calendar"></i>
        <span class="text-xs opacity-85 truncate">{format(new Date(activity?.start_date ?? 0), 'EEE, MMM d · hh:mm aa')}</span>
      </div>
    </div>
  </div>

  <!-- Moving peloton strip -->
  <div class="peloton-strip" aria-hidden="true">
    <i class="fas fa-person-running peloton-bike" style="animation-duration: 4500ms; animation-direction: reverse;"></i>
    <i class="fas fa-person-running peloton-bike" style="animation-duration: 4900ms; animation-direction: reverse;"></i>
    <i class="fas fa-person-running peloton-bike" style="animation-duration: 5300ms; animation-direction: reverse;"></i>
    <i class="fas fa-person-running peloton-bike" style="animation-duration: 5500ms; animation-direction: reverse;"></i>
    <i class="fas fa-person-running peloton-bike" style="animation-duration: 5700ms; animation-direction: reverse;"></i>
    <i class="fas fa-person-running peloton-bike" style="animation-duration: 5900ms; animation-direction: reverse;"></i>
    <i class="fas fa-person-running peloton-bike" style="animation-duration: 6300ms; animation-direction: reverse;"></i>
    <i class="fas fa-person-running peloton-bike" style="animation-duration: 6700ms; animation-direction: reverse;"></i>
  </div>
</div>

<style>
  /* Accent theme variables */
  .group { --accent: #FF6B6B; --accent-rgb: 255,107,107; }
  /* Card background: dark gradient + subtle pattern */
  .card-bg {
    /* playful, distinctive tinted background with dots + soft stripes + color glows */
    background:
      radial-gradient(circle at 12px 12px, rgba(255,255,255,0.05) 1px, transparent 1px),
      radial-gradient(circle at 36px 24px, rgba(255,255,255,0.04) 1px, transparent 1px),
      radial-gradient(circle at 18px 36px, rgba(255,255,255,0.03) 1px, transparent 1px),
      repeating-linear-gradient(45deg, rgba(var(--accent-rgb),0.035) 0 6px, rgba(var(--accent-rgb),0) 6px 14px),
      radial-gradient(120% 80% at 0% 0%, rgba(var(--accent-rgb),0.09) 0%, rgba(0,0,0,0) 60%),
      radial-gradient(100% 90% at 100% 100%, rgba(155,93,229,0.07) 0%, rgba(0,0,0,0) 55%),
      linear-gradient(180deg, #0a151a 0%, #0d1f26 55%, #0a1418 100%);
    background-size: 48px 48px, 48px 48px, 48px 48px, auto, auto, auto, auto;
  }
  /* Peloton graphic (abstract riders) */
  .peloton {
    width: 100%;
    height: 100%;
    background-image: radial-gradient(circle at 20% 60%, rgba(255,255,255,0.25) 2px, transparent 3px),
                      radial-gradient(circle at 40% 40%, rgba(255,255,255,0.25) 2px, transparent 3px),
                      radial-gradient(circle at 60% 55%, rgba(255,255,255,0.25) 2px, transparent 3px),
                      radial-gradient(circle at 80% 35%, rgba(255,255,255,0.25) 2px, transparent 3px),
                      radial-gradient(circle at 30% 70%, rgba(255,255,255,0.25) 2px, transparent 3px),
                      radial-gradient(circle at 70% 30%, rgba(255,255,255,0.25) 2px, transparent 3px);
    background-size: 100% 100%;
    opacity: 0.8;
  }

  @keyframes pelotonDriftLeft {
    0% { transform: translateX(0px) translateY(0px); }
    50% { transform: translateX(-8px) translateY(-2px); }
    100% { transform: translateX(0px) translateY(0px); }
  }
  @keyframes pelotonDriftRight {
    0% { transform: translateX(0px) translateY(0px); }
    50% { transform: translateX(10px) translateY(2px); }
    100% { transform: translateX(0px) translateY(0px); }
  }
  .animate-peloton-left { animation: pelotonDriftLeft 5s ease-in-out infinite; }
  .animate-peloton-right { animation: pelotonDriftRight 6s ease-in-out infinite; }

  /* Flow ribbons */
  .ribbon {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    padding: 8px 12px;
    color: #d9e7ea;
    transform: translateY(8px);
    opacity: 0;
  }
  .ribbon::before {
    content: "";
    position: absolute; inset: 0;
    border-radius: 10px;
    background: linear-gradient(90deg, rgba(var(--accent-rgb),0.16), rgba(var(--accent-rgb),0.08) 60%, rgba(var(--accent-rgb),0.02));
    clip-path: polygon(0% 0%, 95% 0%, 100% 50%, 95% 100%, 0% 100%);
  }
  .ribbon-left::before {
    clip-path: polygon(0% 0%, 90% 0%, 95% 50%, 90% 100%, 0% 100%);
  }
  .ribbon-right::before {
    clip-path: polygon(5% 0%, 100% 0%, 100% 100%, 5% 100%, 0% 50%);
  }
  .ribbon-label { font-size: 14px; font-weight: 600; opacity: 0.85; position: relative; }
  .ribbon-value { font-size: 20px; font-weight: 800; color: var(--accent); position: relative; letter-spacing: 0.02em; }
  .ribbon-unit { font-size: 11px; opacity: 0.8; position: relative; }
  @keyframes ribbonInLeft {
    0% { transform: translateX(-16px) translateY(8px) scale(0.98); opacity: 0; }
    100% { transform: translateX(0px) translateY(0px) scale(1); opacity: 1; }
  }
  @keyframes ribbonInRight {
    0% { transform: translateX(16px) translateY(8px) scale(0.98); opacity: 0; }
    100% { transform: translateX(0px) translateY(0px) scale(1); opacity: 1; }
  }
  .ribbon-in { animation-duration: 650ms; animation-timing-function: cubic-bezier(0.2, 0.8, 0.2, 1); animation-fill-mode: forwards; animation-delay: var(--delay, 0ms); }
  .ribbon-left.ribbon-in { animation-name: ribbonInLeft; }
  .ribbon-right.ribbon-in { animation-name: ribbonInRight; }

  /* Divider to delineate the two stat ribbons */
  .divider { height: 1px; margin: 2px 8px; background: linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,0.12), rgba(255,255,255,0)); }

  /* Map reveal */
  .map-frame { position: relative; opacity: 0; }
  .map-overlay {
    position: absolute; inset: 0;
    background: radial-gradient(60% 60% at 50% 50%, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.0) 70%);
    pointer-events: none;
  }
  @keyframes mapIn {
    0% { transform: scale(0.96) translateY(10px); opacity: 0; }
    100% { transform: scale(1.0) translateY(0px); opacity: 1; }
  }
  .map-in img { transform-origin: 50% 50%; }
  .map-in { animation: mapIn 900ms cubic-bezier(0.22, 0.72, 0.25, 1) forwards; animation-delay: 150ms; }

  /* Pop-out animation like AchievementCard */
  .img-pop-in { transform-origin: center center; animation: img-pop-in-key 800ms ease-out both; }
  /* Ensure the image is invisible until animated */
  .map-frame img { opacity: 0; }
  @keyframes img-pop-in-key {
    0% { transform: scale(0); opacity: 0; }
    60% { transform: scale(1.06); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }

  /* Meta lines flowing in */
  .meta-line { display: flex; align-items: center; gap: 8px; color: #d9e7ea; transform: translateY(8px); opacity: 0; }
  @keyframes flowIn {
    0% { transform: translateY(8px); opacity: 0; }
    100% { transform: translateY(0px); opacity: 1; }
  }
  .flow-in { animation: flowIn 560ms ease-out forwards; animation-delay: var(--delay, 0ms); }


  /* Peloton strip: tiny bike icons gliding across bottom */
  .peloton-strip {
    position: absolute;
    left: 0; right: 0; bottom: 6px;
    display: flex; gap: 18px; justify-content: center; align-items: center;
    opacity: 0.18;
    pointer-events: none;
  }
  .peloton-bike {
    color: var(--accent);
    filter: drop-shadow(0 2px 2px rgba(0,0,0,0.35));
    animation: bike-glide linear infinite;
  }
  @keyframes bike-glide {
    0% { transform: translateX(300px) translateY(0) scale(1); }
    100%{ transform: translateX(-300px) translateY(0) scale(1); }
  }

</style>
